import Box from '@mui/material/Box';
import InputNumber from 'common/components/Elements/Form/InputNumber/InputNumber';
import ModalActions from 'common/components/Elements/Modal/ModalActions/ModalActions';
import SimpleModal from 'common/components/Elements/Modal/SimpleModal/SimpleModal';
import { stringIsNumber } from 'common/utilities/core/string';
import type { AppDispatch } from 'core/redux/store/configureStore.types';
import { unmatchedConstraintsErrorMsg } from 'core/utilities/constraint/constraint';
import I18n from 'i18n/en-US';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import type { SequenceAlign } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import { clearSelection, createSelection } from 'sequenceAnalyser/redux/actions/sequenceAnalyserActions';
import type { SequenceSelection } from 'sequenceAnalyser/utilities/selection/selection';

export interface ComponentProps {
  open: boolean;
  onOpen: (open: boolean) => void;
  selection: SequenceSelection;
  alignedSequences: SequenceAlign[];
}

export function EditSelectionRangeModal({
  open, onOpen, selection, alignedSequences,
}: ComponentProps) {
  const dispatch = useDispatch<AppDispatch>();
  const maxLength = alignedSequences[0].residues.length;

  const [positionStart, setPositionStart] = useState((
    selection.positionStart === undefined
      ? 1
      : selection.positionStart + 1
  ).toString());
  const [positionEnd, setPositionEnd] = useState((
    selection.positionEnd === undefined
      ? maxLength
      : selection.positionEnd + 1
  ).toString());

  const fromErrorMsg = unmatchedConstraintsErrorMsg({
    mandatory: true,
    range: { min: 1, max: maxLength },
    precision: { integerDigits: maxLength.toString().length, fractionalDigits: 0 },
  }, [], positionStart);

  const toErrorMsg = unmatchedConstraintsErrorMsg({
    mandatory: true,
    range: { min: stringIsNumber(positionStart) ? Number(positionStart) : 1, max: maxLength },
    precision: { integerDigits: maxLength.toString().length, fractionalDigits: 0 },
  }, [], positionEnd);

  const disableApply = fromErrorMsg.length > 0 || toErrorMsg.length > 0;

  return (
    <SimpleModal
      title={I18n.SequenceAnalyser.TableActions.EditSelectionRangeModal.title}
      open={open}
      onClose={() => {
        onOpen(false);
      }}
      size="auto"
      content={(
        <Box mt={1}>
          <InputNumber
            label={I18n.SequenceAnalyser.TableActions.EditSelectionRangeModal.from}
            value={positionStart}
            onChange={((value) => {
              setPositionStart(value);
            })}
            fullWidth
            required
            errorMsg={fromErrorMsg}
          />
          <InputNumber
            label={I18n.SequenceAnalyser.TableActions.EditSelectionRangeModal.to}
            value={positionEnd}
            onChange={((value) => {
              setPositionEnd(value);
            })}
            fullWidth
            required
            errorMsg={toErrorMsg}
          />
          <ModalActions
            applyText={I18n.SequenceAnalyser.TableActions.EditSelectionRangeModal.save}
            disableApply={disableApply}
            applyTitle={disableApply ? I18n.SequenceAnalyser.TableActions.EditSelectionRangeModal.disable : ''}
            onApply={() => {
              dispatch(clearSelection());

              if (Number(positionStart) === 1 && Number(positionEnd) === maxLength) {
                dispatch(createSelection({
                  ids: selection.ids,
                  positionStart: undefined,
                  positionEnd: undefined,
                }));
              } else {
                dispatch(createSelection({
                  ids: selection.ids,
                  positionStart: Number(positionStart) - 1,
                  positionEnd: Number(positionEnd) - 1,
                }));
              }

              // Close modal
              onOpen(false);
            }}
          />
        </Box>
      )}
    />
  );
}

export default EditSelectionRangeModal;
